import { useLayoutEffect, useRef, type ChangeEvent } from "react"

import {
  countPhoneSignificantChars,
  formatPhoneAsYouType,
} from "@/lib/phone-input-utils"
import type { CountryCode } from "@/lib/phone-utils"

interface UsePhoneInputOptions {
  value: string
  onValueChange: (value: string) => void
  defaultCountry?: CountryCode
  enabled?: boolean
}

function caretAfterSignificantChars(value: string, count: number) {
  if (count <= 0) return 0
  let seen = 0
  for (let i = 0; i < value.length; i++) {
    if (/[\d+]/.test(value[i])) seen++
    if (seen === count) return i + 1
  }
  return value.length
}

function hasFormattingChars(value: string) {
  return countPhoneSignificantChars(value) < value.length
}

export function usePhoneInput({
  value,
  onValueChange,
  defaultCountry = "US",
  enabled = true,
}: UsePhoneInputOptions) {
  const ref = useRef<HTMLInputElement>(null)
  const pendingCaretRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    const caret = pendingCaretRef.current
    const input = ref.current
    if (caret == null || !input) return
    pendingCaretRef.current = null
    if (document.activeElement === input) {
      input.setSelectionRange(caret, caret)
    }
  }, [value])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (!enabled) {
      onValueChange(raw)
      return
    }

    const formatted = formatPhoneAsYouType(raw, value, defaultCountry)
    // AsYouType gives up on formatting once a number is too long, which would
    // flatten "(415) 555-1234" into raw digits on one extra keystroke.
    if (
      raw.length > value.length &&
      !hasFormattingChars(formatted) &&
      hasFormattingChars(value)
    ) {
      onValueChange(value)
      return
    }
    const selection = e.target.selectionStart ?? raw.length
    if (selection < raw.length) {
      const dropped =
        countPhoneSignificantChars(raw) - countPhoneSignificantChars(formatted)
      const significantBeforeCaret =
        countPhoneSignificantChars(raw.slice(0, selection)) -
        Math.max(dropped, 0)
      pendingCaretRef.current = caretAfterSignificantChars(
        formatted,
        significantBeforeCaret
      )
    } else {
      pendingCaretRef.current = formatted.length
    }
    onValueChange(formatted)
  }

  return { ref, onChange }
}
